/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  CircularProgress,
  FormControlLabel,
  Grid,
  styled,
  Switch,
} from '@mui/material';
import CurrencyFormat from 'components/ui/text-formats/CurrencyFormat';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useTopPanel from 'hooks/useTopPanel';
import moment from 'moment';
import { Fragment, memo, useEffect, useState } from 'react';
import { useQueries } from 'react-query';
import { Link, useParams } from 'react-router-dom';
import ContractCancellationModal from './ContractCancellationModal';
import InfoTooltip from 'components/ui/tooltips/InfoTooltip';
import DangerTooltip from 'components/ui/tooltips/DangerTooltip';
import BaseTooltipCustomWidth from 'components/ui/tooltips/BaseTooltipCustomWidth';
import ImagePreviewDialog from 'components/ui/dialogs/ImagePreviewDialog';
import { useTranslation } from 'react-i18next';
import PaymentAddModal from '../payment/PaymentAddModal';
import useMenu from 'hooks/useMenu';
import useAccess from 'hooks/useAccess';
import ContractDownloaderByLang from './ContractDownloaderByLang';
import ContractInfoTables from './ContractInfoTables';
import ContractViewClientInfo from './ContractViewClientInfo';
import AnimatedDiscountInfo from './AnimatedDiscountInfo';
import FilesModal from './Uploader/FileImageModal';
import CommentModal from './Comment/CommentModal';
import SentMessages from './SentMessages';
import HomesSwiper from './HomesSwiper';
import ContractTasksModal from './Tasks/ContractTasksModal';
import ContractTasksSection from './Tasks/ContractTasksSection';
import ContractViewModern from './ContractViewModern';

const Android12Switch = styled(Switch)(({ theme }) => ({
  padding: 8,
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 16,
      height: 16,
    },
    '&::before': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 12,
    },
    '&::after': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 12,
    },
  },
  '& .MuiSwitch-thumb': {
    boxShadow: 'none',
    width: 16,
    height: 16,
    margin: 2,
  },
}));

const ContractView = () => {
  const { id } = useParams();
  const { hasAccess } = useAccess();
  const { t, i18n } = useTranslation();
  const { hasChild } = useMenu();
  const axiosPrivate = useAxiosPrivate();
  const [hasError, setHasError] = useState(false);
  const [refetch, setRefetch] = useState(false);
  const [totalLeftByMonth, setTotalLeftByMonth] = useState(0);
  const [cancellationOpen, setCancellationOpen] = useState(false);
  const [uploaderOpen, setUploaderOpen] = useState(false);
  const [commentOpen, setCommentOpen] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [isNotary, setIsNotary] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  const [openTasksModal, setOpenTasksModal] = useState(false);
  const [modernView, setModernView] = useState(false);
  const [openHomeLayoutImageDialog, setOpenHomeLayoutImageDialog] =
    useState(false);
  const { setComponent } = useTopPanel();

  const view = (code) => hasChild('CONTRACTS', code);

  const [contractQuery, paymentsListQuery, contractLangs] = useQueries([
    {
      queryKey: 'contractSingle',
      queryFn: async function () {
        const response = await axiosPrivate.get(`/admin/contract/edit/${id}`);
        return response.data.data;
      },
      onSuccess: (data) => {
        if (data) {
          setIsNotary(data?.is_notarial == 1);
          setIsComplete(data?.is_complete == 1);
        }
      },
      enabled: !hasError && !!id,
      onError: (error) => {
        setHasError(true);
      },
      retry: false,
    },
    {
      queryKey: 'contractPaymentList',
      queryFn: async function () {
        const response = await axiosPrivate.get(`/admin/contract/list/${id}`);
        return response.data.data;
      },
      onSuccess: (data) => {
        let leftTotal = 0;
        data.forEach((item, index) => {
          if (moment(item.payment_date, 'YYYY-MM-DD').isBefore(moment())) {
            leftTotal += +item.left;
          }
        });
        setTotalLeftByMonth(leftTotal);
      },
      enabled: !hasError && !!id,
      onError: (error) => {
        setHasError(true);
      },
      retry: false,
    },
    {
      queryKey: '/dictionary/contracttemps/{id}',
      queryFn: async function () {
        const response = await axiosPrivate.get(
          `/dictionary/contracttemps/${id}`
        );
        return response.data.data;
      },
      // onSuccess: (data) => {
      //   let leftTotal = 0;
      //   data.forEach((item, index) => {
      //     if (moment(item.payment_date, 'YYYY-MM-DD').isBefore(moment())) {
      //       leftTotal += +item.left;
      //     }
      //   });
      //   setTotalLeftByMonth(leftTotal);
      // },
      enabled: !hasError && !!id,
      onError: (error) => {
        setHasError(true);
      },
      retry: false,
    },
  ]);

  useEffect(() => {
    setComponent(
      <div className='flex flex-row items-center'>
        <div
          className={`component-title flex flex-row ${
            contractQuery?.data?.status != 3 ? '' : '!text-red-600'
          }`}
        >
          {t('contract.view.title')} №{' '}
          <BaseTooltipCustomWidth
            arrow={true}
            placement='bottom'
            enterDelay={1000}
            leaveTouchDelay={0}
            title={contractQuery?.data?.name}
            width={'800px'}
            fontSize={'1rem'}
          >
            <span className='text-line-1 max-w-[800px] ml-1'>
              {contractQuery?.data?.name}
            </span>
          </BaseTooltipCustomWidth>
          {contractQuery.data?.date && (
            <span className='ml-4'>
              {moment(contractQuery.data?.date, 'YYYY-MM-DD').format(
                'DD.MM.YYYY'
              )}
            </span>
          )}
          {contractQuery.data?.cancel_date &&
          contractQuery?.data?.status == 3 ? (
            <span className='ml-4'>
              {t('common.typeList.contractStatus.cancel')}:{' '}
              {moment(contractQuery.data?.cancel_date, 'YYYY-MM-DD').format(
                'DD.MM.YYYY'
              )}
            </span>
          ) : (
            ''
          )}
          <button
            onClick={() => setModernView((v) => !v)}
            className='ml-4 flex items-center gap-1.5 px-3 py-1 rounded-lg text-sm font-medium border transition-colors'
            style={modernView
              ? { background: '#6366f1', borderColor: '#6366f1', color: '#fff' }
              : { background: '#f5f5ff', borderColor: '#c7d2fe', color: '#6366f1' }
            }
          >
            <i className={`bi ${modernView ? 'bi-layout-text-sidebar' : 'bi-grid-1x2'} text-xs`} />
            {modernView ? t('contract.view.classicView') : t('contract.view.modernView')}
          </button>
          {contractQuery.data?.type_name?.name && (
            <span className='ml-4 border border-indigo-700 bg-indigo-50 rounded-md px-2'>
              {contractQuery.data?.type_name?.name}
            </span>
          )}
        </div>
      </div>
    );
  }, [contractQuery?.data, i18n.language, modernView, setModernView]);

  const notaryMutate = async (check) => {
    try {
      const response = await axiosPrivate.post(
        `/admin/contract/updatenatarius/${id}`,
        JSON.stringify({ check }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response?.data?.status) {
        setIsNotary(check == 1);
      }
    } catch (error) {}
  };

  const complete = async (check) => {
    try {
      const response = await axiosPrivate.post(
        `/admin/contract/updatecomplete/${id}`,
        JSON.stringify({ check }),
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response?.data?.status) {
        setIsComplete(check == 1);
      }
    } catch (error) {}
  };

  const onChangeComplete = (e) => {
    complete(e?.target?.checked ? 1 : 0);
  };

  const onChange = (e) => {
    notaryMutate(e?.target?.checked ? 1 : 0);
  };

  return (
    <div className='component-add-edit-wrapper mx-4'>
      {contractQuery.isLoading || contractQuery.isFetching ? (
        <div className='circular-progress-box py-5'>
          <CircularProgress size={35} />
        </div>
      ) : (
        contractQuery?.data && (
          <Fragment>
            <div className='contract-comment rounded-lg px-4 pb-3 pt-0 w-full flex gap-2 items-center justify-end'>
              {contractQuery?.data?.status != 3 && (
                <>
                  {hasAccess(
                    contractQuery?.data?.homelist?.[0]?.home?.blocks?.objects_id
                  ) &&
                    view('MAKE_NOTARY') && (
                      <InfoTooltip
                        arrow={true}
                        placement='top'
                        title={t('contract.completed')}
                      >
                        <FormControlLabel
                          control={
                            <Android12Switch
                              checked={isComplete}
                              onChange={onChangeComplete}
                            />
                          }
                          className='[&>.MuiFormControlLabel-label]:!text-[12px]'
                          label={t('contract.completed')}
                        />
                      </InfoTooltip>
                    )}
                  {hasAccess(
                    contractQuery?.data?.homelist?.[0]?.home?.blocks?.objects_id
                  ) &&
                    view('MAKE_NOTARY') && (
                      <InfoTooltip
                        arrow={true}
                        placement='top'
                        title={t('common.fields.notary')}
                      >
                        <FormControlLabel
                          control={
                            <Android12Switch
                              checked={isNotary}
                              onChange={onChange}
                            />
                          }
                          className='[&>.MuiFormControlLabel-label]:!text-[12px] max-2xl:!text-[10px]'
                          label={t('common.fields.notary')}
                        />
                      </InfoTooltip>
                    )}
                  {contractQuery?.data?.status != '2' &&
                    hasAccess(
                      contractQuery?.data?.homelist?.[0]?.home?.blocks
                        ?.objects_id
                    ) &&
                    view('MAKE_PAYMENT') && (
                      <Button
                        // size='small'
                        color='primary'
                        variant='contained'
                        className='!text-[12px]'
                        onClick={() => setOpenPaymentModal(true)}
                        startIcon={
                          <i className='bi bi-plus-circle-fill !text-sm' />
                        }
                      >
                        {t('common.button.pay')}
                      </Button>
                    )}
                </>
              )}

              <Button
                // size='small'
                color='primary'
                variant='base'
                className='!text-[12px]'
                onClick={() => setUploaderOpen(true)}
                startIcon={<i className='bi bi-upload !text-sm' />}
              >
                {t('common.fields.uploadFile')}
              </Button>
              <Button
                // size='small'
                color='primary'
                variant='base'
                className='!text-[12px]'
                onClick={() => setCommentOpen(true)}
                startIcon={<i className='bi bi-chat-left-text !text-sm' />}
              >
                {t('common.fields.comment')}
              </Button>
              <Button
                // size='small'
                color='primary'
                variant='base'
                className='!text-[12px]'
                onClick={() => setSmsOpen(true)}
                startIcon={<i className='bi bi-envelope-check !text-sm' />}
              >
                {t('settings.sentMessages.title')}
              </Button>
              {hasChild('STAFF_TASKS', 'STAFF_VIEW_TASKS') && (
                <Button
                  color='primary'
                  variant='base'
                  className='!text-[12px]'
                  onClick={() => setOpenTasksModal(true)}
                  startIcon={<i className='bi bi-journal-check !text-sm' />}
                >
                  {t('tasks.staffTask.title')}
                </Button>
              )}

              {contractQuery?.data?.status != 3 && (
                <>
                  <Link
                    className='no-underline'
                    to={`/admin/client/view/${contractQuery.data?.custom?.id}`}
                  >
                    <Button
                      size='small'
                      variant='base'
                      className='!text-[12px]'
                      startIcon={
                        <i className='bi bi-journal-check  !text-sm' />
                      }
                    >
                      CRM
                    </Button>
                  </Link>

                  <ContractDownloaderByLang
                    contractLangs={contractLangs}
                    contractQuery={contractQuery}
                  />

                  <InfoTooltip
                    arrow={true}
                    placement='top'
                    title={t('contract.view.action.refresh')}
                  >
                    <Button
                      variant='base'
                      color='info'
                      size='small'
                      className='!text-[12px]'
                      sx={{ width: 40 }}
                      onClick={contractQuery.refetch}
                    >
                      <i className='bi bi-arrow-repeat' />
                    </Button>
                  </InfoTooltip>

                  {hasAccess(
                    contractQuery?.data?.homelist?.[0]?.home?.blocks?.objects_id
                  ) &&
                    view('CANCEL_CONTRACT') && (
                      <>
                        {contractQuery?.data?.status == '2' && (
                          <Button
                            color='error'
                            variant='contained'
                            className='!text-[12px]'
                          >
                            <span>{t('contract.view.completed')}</span>
                          </Button>
                        )}
                        <DangerTooltip
                          arrow={true}
                          placement='top'
                          color='error'
                          title={t('contract.view.action.delete')}
                        >
                          <Button
                            color='error'
                            sx={{ width: 40, minWidth: 40, height: 40 }}
                            variant='contained'
                            onClick={() => setCancellationOpen(true)}
                          >
                            <i className='bi bi-x !text-[12px]' />
                            {/* <span>{t('common.button.cancel')}</span> */}
                          </Button>
                        </DangerTooltip>
                      </>
                    )}
                </>
              )}
            </div>
            {modernView ? (
              <ContractViewModern
                contractQuery={contractQuery}
                paymentsListQuery={paymentsListQuery}
                id={id}
                totalLeftByMonth={totalLeftByMonth}
                refetch={refetch}
                setRefetch={setRefetch}
              />
            ) : (
              <>
                <div className='component-add-edit-header my-3'>
                  <Grid container spacing={2} columns={{ xs: 12, sm: 12, lg: 12 }}>
                    <Grid item={true} lg={2} md={2} sm={6} xs={12}>
                      <Button
                        variant='base'
                        sx={{
                          borderRadius: '15px',
                          paddingLeft: '7px',
                          paddingRight: '7px',
                          '&:hover .leading-5': {
                            color: '#fff',
                          },
                          position: 'relative',
                          overflow: 'hidden',
                          justifyContent: 'flex-start',
                        }}
                        className='!h-full'
                        fullWidth
                      >
                        <AnimatedDiscountInfo
                          data={contractQuery?.data}
                          from='sum'
                        />
                      </Button>
                    </Grid>
                    <Grid item={true} lg={2} md={2} sm={6} xs={12}>
                      <Button
                        variant='base'
                        sx={{
                          borderRadius: '15px',
                          paddingLeft: '7px',
                          paddingRight: '7px',
                          '&:hover .leading-5': {
                            color: '#fff',
                          },
                        }}
                        className='!h-full'
                        fullWidth
                      >
                        <div className='flex flex-col p-2 w-full items-start'>
                          <span className='text-[14px] text-[#979797] leading-5 mb-1'>
                            {t('contract.view.header.startPrice')}
                          </span>
                          <span className='text-[18px] text-black'>
                            <CurrencyFormat
                              allowNegative
                              value={contractQuery?.data?.start_price}
                              suffix={
                                contractQuery?.data?.isvalute == '1' ? ' $' : ' UZS'
                              }
                            />
                          </span>
                        </div>
                      </Button>
                    </Grid>
                    <Grid item={true} lg={2} md={2} sm={6} xs={12}>
                      <Button
                        variant='base'
                        sx={{
                          borderRadius: '15px',
                          paddingLeft: '7px',
                          paddingRight: '7px',
                          '&:hover .leading-5': {
                            color: '#fff',
                          },
                        }}
                        className='!h-full'
                        fullWidth
                      >
                        <div className='flex flex-col p-2 w-full items-start'>
                          <span className='text-[14px] text-[#979797] leading-5 mb-1'>
                            {t('payment.add.contract.paidSum')}
                          </span>

                          {contractQuery?.data?.isvalute == '2' ? (
                            <>
                              <span className='text-[18px] text-black'>
                                <CurrencyFormat
                                  allowNegative
                                  value={
                                    contractQuery?.data?.totalsum -
                                    contractQuery?.data?.leftsum
                                  }
                                  suffix={' UZS'}
                                />
                              </span>
                              <span className='text-[18px] text-black'>
                                <CurrencyFormat
                                  allowNegative
                                  value={
                                    contractQuery?.data?.totalvalute -
                                    contractQuery?.data?.leftvalute
                                  }
                                  suffix={' $'}
                                />
                              </span>
                            </>
                          ) : (
                            <span className='text-[18px] text-black'>
                              <CurrencyFormat
                                allowNegative
                                value={
                                  contractQuery?.data?.sum -
                                  contractQuery?.data?.left -
                                  contractQuery?.data?.discount
                                }
                                suffix={
                                  contractQuery?.data?.isvalute == '1'
                                    ? ' $'
                                    : ' UZS'
                                }
                              />
                            </span>
                          )}
                        </div>
                      </Button>
                    </Grid>
                    <Grid item={true} lg={2} md={2} sm={6} xs={12}>
                      <Button
                        variant='base'
                        sx={{
                          borderRadius: '15px',
                          paddingLeft: '7px',
                          paddingRight: '7px',
                          '&:hover .leading-5': {
                            color: '#fff',
                          },
                          position: 'relative',
                          overflow: 'hidden',
                          justifyContent: 'flex-start',
                        }}
                        className='!h-full'
                        fullWidth
                      >
                        <AnimatedDiscountInfo
                          data={contractQuery?.data}
                          from='square'
                        />
                      </Button>
                    </Grid>
                    <Grid item={true} lg={2} md={2} sm={6} xs={12}>
                      <Button
                        variant='base'
                        sx={{
                          borderRadius: '15px',
                          paddingLeft: '7px',
                          paddingRight: '7px',
                          '&:hover .leading-5': {
                            color: '#fff',
                          },
                        }}
                        fullWidth
                        className='!h-full'
                      >
                        <div className='flex flex-col p-2 w-full items-start'>
                          <span className='text-[14px] text-[#979797] leading-5 mb-1'>
                            {t('contract.view.header.monthlyLeft')}
                          </span>
                          <span className='text-[18px] text-black'>
                            {totalLeftByMonth ? (
                              <CurrencyFormat
                                value={totalLeftByMonth}
                                allowNegative={true}
                                suffix={
                                  contractQuery?.data?.isvalute == '0'
                                    ? ' UZS'
                                    : ' $'
                                }
                              />
                            ) : (
                              t('contract.view.header.noMonthlyLeft')
                            )}
                          </span>
                        </div>
                      </Button>
                    </Grid>
                    <Grid item={true} lg={2} md={2} sm={6} xs={12}>
                      <Button
                        variant='base'
                        sx={{
                          borderRadius: '15px',
                          paddingLeft: '7px',
                          paddingRight: '7px',
                          '&:hover .leading-5': {
                            color: '#fff',
                          },
                        }}
                        className='!h-full'
                        fullWidth
                      >
                        <div className='flex flex-col p-2 w-full items-start'>
                          <span className='text-[14px] text-[#979797] leading-5 mb-1'>
                            {t('contract.view.header.leftPrice')}
                          </span>
                          {contractQuery?.data?.isvalute == '2' ? (
                            <>
                              <span className='text-[18px] text-black'>
                                <CurrencyFormat
                                  allowNegative
                                  value={contractQuery?.data?.leftsum}
                                  suffix={' UZS'}
                                />
                              </span>
                              <span className='text-[18px] text-black'>
                                <CurrencyFormat
                                  allowNegative
                                  value={contractQuery?.data?.leftvalute}
                                  suffix={' $'}
                                />
                              </span>
                            </>
                          ) : (
                            <span className='text-[18px] text-black'>
                              <CurrencyFormat
                                value={contractQuery?.data?.left}
                                allowNegative={true}
                                suffix={
                                  contractQuery?.data?.isvalute == '1'
                                    ? ' $'
                                    : ' UZS'
                                }
                              />
                            </span>
                          )}
                        </div>
                      </Button>
                    </Grid>
                  </Grid>
                </div>
                <Grid container spacing={2} columns={{ xs: 12, sm: 12, lg: 12 }}>
                  <Grid item lg={7} md={7} sm={7} xs={7}>
                    <ContractViewClientInfo contractQuery={contractQuery} />
                  </Grid>
                  <Grid item lg={5} md={5} sm={5} xs={5}>
                    <HomesSwiper
                      homes={
                        contractQuery?.data?.homelist || [
                          { home: contractQuery?.data?.homes },
                        ]
                      }
                    />
                  </Grid>
                  <Grid item lg={12} md={12} sm={12} xs={12}>
                    <div>
                      <div className='mt-4 mb-6'>
                        {id && (
                          <ContractInfoTables
                            id={id}
                            contractQuery={contractQuery}
                            paymentsListQuery={paymentsListQuery}
                            refetch={refetch}
                            setRefetch={setRefetch}
                          />
                        )}
                      </div>
                    </div>
                  </Grid>
                </Grid>
              </>
            )}

            {/* Topshiriqlar — faqat mavjud bo'lsa (To'lov tarixi/grafigidan keyin) */}
            {contractQuery?.data?.tasks?.length > 0 && (
              <ContractTasksSection tasks={contractQuery.data.tasks} />
            )}
          </Fragment>
        )
      )}
      {openHomeLayoutImageDialog &&
        contractQuery?.data?.homes?.plan &&
        contractQuery?.data?.homes?.plan?.link && (
          <ImagePreviewDialog
            open={openHomeLayoutImageDialog}
            setOpen={setOpenHomeLayoutImageDialog}
            url={contractQuery?.data?.homes?.plan?.link}
          />
        )}

      {cancellationOpen && view('CANCEL_CONTRACT') && (
        <ContractCancellationModal
          open={cancellationOpen}
          setOpen={setCancellationOpen}
          data={contractQuery?.data}
          refetch={contractQuery.refetch}
        />
      )}
      {uploaderOpen && (
        <FilesModal
          open={uploaderOpen}
          setOpen={setUploaderOpen}
          data={contractQuery?.data}
          refetch={contractQuery.refetch}
        />
      )}
      {commentOpen && (
        <CommentModal
          open={commentOpen}
          setOpen={setCommentOpen}
          data={contractQuery?.data}
          refetch={contractQuery.refetch}
        />
      )}
      {smsOpen && (
        <SentMessages
          open={smsOpen}
          setOpen={setSmsOpen}
          data={contractQuery?.data}
          refetch={contractQuery.refetch}
        />
      )}

      {openPaymentModal && view('MAKE_PAYMENT') && (
        <PaymentAddModal
          open={openPaymentModal}
          setOpen={setOpenPaymentModal}
          refetch={() => {
            contractQuery.refetch();
            paymentsListQuery.refetch();
            setRefetch(true);
          }}
          data={contractQuery?.data}
        />
      )}
      {openTasksModal && hasChild('STAFF_TASKS', 'STAFF_VIEW_TASKS') && (
        <ContractTasksModal
          open={openTasksModal}
          setOpen={setOpenTasksModal}
          contractId={id}
          tasks={contractQuery?.data?.tasks || []}
          refetchFn={contractQuery.refetch}
        />
      )}
    </div>
  );
};

export default memo(ContractView);
